import React, { useMemo } from "react";
import { TrendingUp, Briefcase, Wallet, Gift, Receipt } from "lucide-react";
import { K } from "../theme";
import { Bg } from "./index";

// Phase 5 — CTC summary card.
// Reads the same per-employee structure as the editor and renders an
// annual view: monthly take-home estimate, total CTC, and the benefits
// row (employer-side costs that don't show on the payslip but are part
// of the offer letter / total cost). Pure presentational — accepts the
// structure response as a prop, no fetching of its own.

const fmtINR = (n) => '₹' + Number(Math.round(n || 0)).toLocaleString('en-IN');
const CATEGORY_LABEL = { earning: 'Earnings', deduction: 'Deductions',
                         reimbursement: 'Reimbursements', benefit: 'Benefits' };
const CATEGORY_ACCENT = { earning: K.g, deduction: K.r, reimbursement: K.bl, benefit: K.pu };

// Same compute logic as the editor / processor. If annualCtcAnchor is
// passed, percent_of_ctc components use it directly; otherwise we
// back-solve from the fixed components.
const computeMonthly = (lines, annualCtcAnchor) => {
  const earnings   = lines.filter(l => l.category === 'earning'   && l.active !== false);
  const deductions = lines.filter(l => l.category === 'deduction' && l.active !== false);
  const reimbursements = lines.filter(l => l.category === 'reimbursement' && l.active !== false);
  const benefits   = lines.filter(l => l.category === 'benefit'   && l.active !== false);

  const basicLine = earnings.find(l => l.componentCode === 'BASIC');
  let basicMonthly = basicLine && basicLine.calcType === 'fixed' ? parseFloat(basicLine.value) || 0 : 0;
  const anchor = parseFloat(annualCtcAnchor) || 0;
  let ctcEstimate = 0;

  if (anchor > 0) {
    ctcEstimate = anchor;
    if (basicLine && basicLine.calcType === 'percent_of_ctc') {
      basicMonthly = (parseFloat(basicLine.value) || 0) / 100 * anchor / 12;
    }
  } else {
    earnings.forEach(l => {
      const v = parseFloat(l.value) || 0;
      if (l.calcType === 'fixed') ctcEstimate += v * 12;
      else if (l.calcType === 'percent_of_basic') ctcEstimate += (basicMonthly * v / 100) * 12;
    });
    if (basicLine && basicLine.calcType === 'percent_of_ctc') {
      let ctc = ctcEstimate || 600000;
      for (let i = 0; i < 6; i++) {
        const basicAnnual = (parseFloat(basicLine.value) || 0) / 100 * ctc;
        basicMonthly = basicAnnual / 12;
        ctc = 0;
        earnings.forEach(l => {
          const v = parseFloat(l.value) || 0;
          if (l.calcType === 'fixed') ctc += v * 12;
          else if (l.calcType === 'percent_of_basic') ctc += basicAnnual * v / 100;
          else if (l.calcType === 'percent_of_ctc' && l.componentCode === 'BASIC') ctc += basicAnnual;
        });
        const otherPctOfCtc = earnings
          .filter(l => l.calcType === 'percent_of_ctc' && l.componentCode !== 'BASIC')
          .reduce((s, l) => s + (parseFloat(l.value) || 0), 0);
        if (otherPctOfCtc > 0) ctc = ctc / (1 - otherPctOfCtc / 100);
      }
      ctcEstimate = ctc;
    }
  }

  const byCode = {};
  const resolve = (l) => {
    const v = parseFloat(l.value) || 0;
    if (l.calcType === 'fixed') return v;
    if (l.calcType === 'percent_of_basic') return basicMonthly * v / 100;
    if (l.calcType === 'percent_of_ctc')   return (ctcEstimate * v / 100) / 12;
    return 0;
  };
  earnings.forEach(l => byCode[l.componentCode] = resolve(l));
  const grossMonthly = earnings.reduce((s, l) => s + byCode[l.componentCode], 0);
  deductions.forEach(l => {
    const v = parseFloat(l.value) || 0;
    if (l.calcType === 'percent_of_gross') byCode[l.componentCode] = grossMonthly * v / 100;
    else byCode[l.componentCode] = resolve(l);
  });
  reimbursements.forEach(l => byCode[l.componentCode] = resolve(l));
  benefits.forEach(l => byCode[l.componentCode] = resolve(l));

  const dedMonthly      = deductions.reduce((s, l) => s + byCode[l.componentCode], 0);
  const benefitsMonthly = benefits.reduce((s, l) => s + byCode[l.componentCode], 0);
  const reimbMonthly    = reimbursements.reduce((s, l) => s + byCode[l.componentCode], 0);
  const reimbAnnual     = reimbMonthly * 12;
  const benefitsAnnual  = benefitsMonthly * 12;
  const dedAnnual       = dedMonthly * 12;
  const grossAnnual     = grossMonthly * 12;

  // Take-home formula per the CHRO spec (HRMS New Updates):
  //   Monthly Take Home = Monthly Gross + Monthly Benefits − Monthly Deductions
  // Benefits here are employer-side contributions (PF_ER, ESI_ER, gratuity
  // provision, GHI), so this is a total-compensation figure.
  const takeHomeMonthly = grossMonthly + benefitsMonthly - dedMonthly;

  // What payroll will actually credit, mirroring routes/payroll.js:
  //   net = gross + reimbursements − deductions
  // Benefits are excluded there (they land in employer_cost) and
  // reimbursements are included — so this deliberately differs from the
  // headline above and is shown beside it rather than left to be
  // discovered on the payslip.
  const netCreditedMonthly = grossMonthly + reimbMonthly - dedMonthly;

  return {
    byCode,
    earnings, deductions, reimbursements, benefits,
    grossMonthly, dedMonthly, benefitsMonthly,
    takeHomeMonthly, netCreditedMonthly,
    grossAnnual, reimbAnnual, benefitsAnnual, dedAnnual,
    totalCtcAnnual: grossAnnual + reimbAnnual + benefitsAnnual,
  };
};

export const EmployeeCtcSummary = ({ structure, annualCtcAnchor }) => {
  // Accept either shape — the editor passes componentName/componentCategory/
  // componentCalcType, the salary-structure API returns plain name/category/
  // calcType. Falling back across both keeps the call sites simple.
  const lines = (structure?.components || []).map(c => ({
    componentCode: c.componentCode,
    name:     c.componentName     || c.name,
    category: c.componentCategory || c.category,
    calcType: c.componentCalcType || c.calcType,
    value:    c.value,
    active:   c.active !== false,
  }));
  const calc = useMemo(() => computeMonthly(lines, annualCtcAnchor), [structure, annualCtcAnchor]);

  if (lines.length === 0) return null;

  return (
    <div className="rounded-xl border border-b0 bg-bg2 overflow-hidden">
      <div className="px-4 py-3 border-b border-b0 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[14px] font-bold text-t0 flex items-center gap-2">
            <TrendingUp size={14}/> CTC Summary
            <Bg c={K.pu} sm>Annual view</Bg>
          </div>
          <div className="text-[14px] text-t3 mt-[2px]">
            Annual breakdown of this employee's compensation — what they take home + what the company pays beyond the payslip.
          </div>
        </div>
        <div className="text-right">
          <div className="text-[12px] uppercase tracking-wide text-t3 font-bold">Total Annual CTC</div>
          <div className="text-[20px] font-extrabold text-t0 font-mono">{fmtINR(calc.totalCtcAnnual)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 p-3">
        <Tile icon={Wallet}     label="Monthly Take-Home"  value={fmtINR(calc.takeHomeMonthly)} accent={K.g}
              hint="Gross + Benefits − Ded." />
        <Tile icon={Briefcase}  label="Annual Gross"       value={fmtINR(calc.grossAnnual)}     accent={K.bl} />
        <Tile icon={Gift}       label="Reimb. (annual)"    value={fmtINR(calc.reimbAnnual)}     accent={K.bl} />
        <Tile icon={TrendingUp} label="Benefits (annual)"  value={fmtINR(calc.benefitsAnnual)}  accent={K.pu} />
        <Tile icon={Receipt}    label="Deductions (annual)" value={fmtINR(calc.dedAnnual)}      accent={K.r} />
      </div>

      {/* Take-Home above follows the configured CTC formula and therefore
          includes employer contributions. Show the actual bank credit next
          to it so payroll queries don't start from a mismatch. */}
      {calc.benefitsMonthly > 0 && (
        <div className="px-3 -mt-1 pb-1 text-[12px] text-t3 leading-[1.4]">
          Monthly Take-Home includes employer-side benefits (PF employer share, gratuity
          provision, insurance). Payroll credits{' '}
          <b className="text-t2 font-mono">{fmtINR(calc.netCreditedMonthly)}</b>
          {' '}— gross + reimbursements − deductions.
        </div>
      )}

      <div className="px-3 pb-3 grid gap-3">
        {['earning', 'reimbursement', 'benefit', 'deduction'].map(cat => {
          const list = calc[cat === 'earning' ? 'earnings'
                          : cat === 'deduction' ? 'deductions'
                          : cat === 'reimbursement' ? 'reimbursements' : 'benefits'];
          if (list.length === 0) return null;
          const total = list.reduce((s, l) => s + (calc.byCode[l.componentCode] || 0), 0) * 12;
          return (
            <div key={cat}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <span className="w-[5px] h-[14px] rounded-full" style={{ background: CATEGORY_ACCENT[cat] }} />
                  <span className="text-[14px] font-bold text-t1">{CATEGORY_LABEL[cat]}</span>
                </div>
                <div className="text-[14px] text-t2 font-mono">{fmtINR(total)} <span className="text-t3">/ yr</span></div>
              </div>
              <div className="rounded-lg border border-b0 overflow-hidden">
                <table className="w-full border-collapse text-[14px]">
                  <tbody>
                    {list.map(l => {
                      const m = calc.byCode[l.componentCode] || 0;
                      return (
                        <tr key={l.componentCode} className="border-b border-b0 last:border-b-0">
                          <td className="px-2 py-1 font-mono text-[14px] text-t3 w-[110px]">{l.componentCode}</td>
                          <td className="px-2 py-1 text-[14px] text-t0">{l.name}</td>
                          <td className="px-2 py-1 text-right text-[14px] text-t2 font-mono w-[110px]">{fmtINR(m)} <span className="text-t3">/mo</span></td>
                          <td className="px-2 py-1 text-right text-[14px] text-t0 font-mono w-[120px]">{fmtINR(m * 12)} <span className="text-t3">/yr</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Tile = ({ icon: Ic, label, value, accent, hint }) => (
  <div className="rounded-lg border border-b0 p-3 bg-bg1">
    <div className="flex items-center gap-2">
      <span className="w-[5px] h-[14px] rounded-full" style={{ background: accent }} />
      <Ic size={11} className="text-t3" />
      <div className="text-[12px] uppercase tracking-wide text-t3 font-bold">{label}</div>
    </div>
    <div className="text-[16px] font-extrabold text-t0 mt-[2px] font-mono">{value}</div>
    {hint && <div className="text-[12px] text-t3 mt-[1px]">{hint}</div>}
  </div>
);
